import React from 'react'
import { useTranslation } from 'react-i18next'
import { zawinNazwe } from '../../lib/zawijanieTekstu'

/**
 * Schemat połączeń: łańcuchy modułów → wejścia MPPT → falownik.
 *
 * Celowo SCHEMAT POGLĄDOWY, nie rysunek wykonawczy — odpowiada na jedno pytanie:
 * „ile modułów w ilu łańcuchach i do którego wejścia je wpiąć". Świadomie NIE MA
 * tu przewodów o przekrojach, zabezpieczeń ani rozłączników: narysowane wyglądałyby
 * na kompletny projekt elektryczny, którym to nie jest.
 *
 * SVG, nie canvas: skaluje się bez rozmycia, tekst da się zaznaczyć i wyszukać,
 * a wydruk (instalator drukuje takie rzeczy) wychodzi ostry w każdej skali.
 *
 * ═══ WYGLĄD (przebudowa 2026-09-30) ═══
 * Ten sam język co raport „Sprawdź ofertę”: bez etykiet WIELKIMI literami, kolor
 * niesie mały znacznik, liczby po polsku (przecinek). Tło w delikatne kropki jak
 * arkusz techniczny, napięcie jako plakietka na przewodzie, magazyn narysowany
 * jak bateria, a dom jak dom: rysunek ma się czytać bez legendy.
 */

// Paleta wejść MPPT — każde wejście ma własny kolor, żeby na pierwszy rzut oka
// było widać, które łańcuchy idą razem.
const KOLORY_MPPT = [
  { linia: '#2563eb', tlo: '#dbeafe', ciemny: '#1e40af' },
  { linia: '#0891b2', tlo: '#cffafe', ciemny: '#155e75' },
  { linia: '#7c3aed', tlo: '#ede9fe', ciemny: '#5b21b6' },
  { linia: '#c2410c', tlo: '#ffedd5', ciemny: '#9a3412' },
]

const TEKST = { mocny: '#0f172a', zwykly: '#334155', cichy: '#64748b', najcichszy: '#94a3b8' }

/** Liczba po polsku: przecinek zamiast kropki (5,46 kWp, a nie 5.46). */
const pl = (v) => String(v).replace('.', ',')

/**
 * Opis napięcia magazynu z `lib/podsumowanieZestawu` („typowo 48 V DC (ok. 40–58 V)”,
 * „51,2 V nom.”) w języku kraju. `lib` składa go po polsku, więc podmieniamy same
 * słowa, a liczby zostają. W Polsce słownik zwraca te same słowa, tekst się nie zmienia.
 */
const opisNapiecia = (t, tekst) => (tekst == null ? tekst : String(tekst)
  .replace(/^typowo /, `${t('schemat.napiecie.typowo')} `)
  .replace(/\(ok\. /, `(${t('schemat.napiecie.ok')} `)
  .replace(/ nominalnie$/, ` ${t('schemat.napiecie.nominalnie')}`)
  .replace(/ nom\.$/, ` ${t('schemat.napiecie.nom')}`))

/** Klasa napięciowa magazynu z `lib/podsumowanieZestawu` → klucz podpisu bloku. */
const KLASY_MAGAZYNU = {
  Wysokonapięciowy: 'schemat.magazyn_wysokie',
  Niskonapięciowy: 'schemat.magazyn_niskie',
  Magazyn: 'schemat.magazyn_bez_klasy',
}

/**
 * Pojedynczy moduł PV.
 *
 * ═══ CZEMU SIATKA, A NIE TRZY KRESKI ═══
 * Wcześniej moduł miał dwie linie poziome i jedną pionową, czyli sześć wielkich
 * pól — z daleka wyglądało to jak okno, nie jak panel. Prawdziwy moduł ma
 * regularną siatkę wielu ogniw i to ONA jest znakiem rozpoznawczym; wystarczy
 * ją zagęścić, żeby rysunek zaczął się czytać bez podpisu.
 *
 * Rama rysowana osobnym prostokątem w środku: aluminiowe obramowanie to
 * najbardziej charakterystyczny element modułu po samej siatce, a jednocześnie
 * oddziela ogniwa od krawędzi, dzięki czemu siatka nie dotyka zaokrągleń.
 *
 * Linie ogniw są CIEŃSZE niż obrys i wyraźnie jaśniejsze — mają budować teksturę,
 * a nie konkurować z konturem, który niesie kolor wejścia MPPT.
 */
const KOLUMN_OGNIW = 3
const WIERSZY_OGNIW = 6
const MARGINES_RAMY = 3
const PRZERWA_OGNIW = 1.1

const ModulPv = ({ x, y, w, h, kolor, gradient }) => {
  const wRamie = w - MARGINES_RAMY * 2
  const hRamie = h - MARGINES_RAMY * 2

  // Bok ogniwa liczony z dostępnego pola po odjęciu WSZYSTKICH przerw między
  // nimi — dzięki temu siatka wypełnia ramę dokładnie, bez resztki po prawej
  // ani na dole, niezależnie od liczby rzędów.
  const wOgniwa = (wRamie - PRZERWA_OGNIW * (KOLUMN_OGNIW - 1)) / KOLUMN_OGNIW
  const hOgniwa = (hRamie - PRZERWA_OGNIW * (WIERSZY_OGNIW - 1)) / WIERSZY_OGNIW

  const ogniwa = []
  for (let kol = 0; kol < KOLUMN_OGNIW; kol += 1) {
    for (let wier = 0; wier < WIERSZY_OGNIW; wier += 1) {
      ogniwa.push({
        klucz: `${kol}-${wier}`,
        x: x + MARGINES_RAMY + kol * (wOgniwa + PRZERWA_OGNIW),
        y: y + MARGINES_RAMY + wier * (hOgniwa + PRZERWA_OGNIW),
      })
    }
  }

  return (
    <g>
      {/* Rama modułu — obrys w kolorze wejścia MPPT, żeby było widać, które
          łańcuchy idą do tego samego wejścia. */}
      <rect x={x} y={y} width={w} height={h} rx="2.5"
            fill={gradient} stroke={kolor.linia} strokeWidth="1.3" />

      {/* KAŻDE OGNIWO OSOBNO, z przerwą dookoła. Wcześniej rysowaliśmy siatkę
          linii — to daje jedną ciągłą płaszczyznę pokreśloną kreskami, czyli
          coś w rodzaju okna, a nie modułu. Prawdziwy panel to policzalne,
          oddzielone od siebie ogniwa i dopiero one czytają się jako panel. */}
      {ogniwa.map((o) => (
        <rect key={o.klucz} x={o.x} y={o.y} width={wOgniwa} height={hOgniwa} rx="0.6"
              fill={kolor.linia} opacity="0.16"
              stroke={kolor.linia} strokeWidth="0.35" strokeOpacity="0.45" />
      ))}
    </g>
  )
}

/**
 * Symbol falownika wg konwencji rysunku elektrycznego: kwadrat przecięty
 * przekątną, po stronie wejścia znak prądu stałego (kreska ciągła nad przerywaną),
 * po stronie wyjścia sinusoida. To ten sam znak, który instalator widzi na
 * schematach jednokreskowych — narysowanie własnej ikonki byłoby ładniejsze
 * i całkowicie nieczytelne dla kogoś z branży.
 */
const SymbolPrzeksztaltnika = ({ x, y, s, kolor = '#334155' }) => {
  const sinusoida = `M ${x + s * 0.5} ${y + s * 0.74}
     c ${s * 0.06} ${-s * 0.2}, ${s * 0.14} ${-s * 0.2}, ${s * 0.2} 0
     c ${s * 0.06} ${s * 0.2}, ${s * 0.14} ${s * 0.2}, ${s * 0.2} 0`
  return (
    <g>
      <rect x={x} y={y} width={s} height={s} rx="4"
            fill="#f8fafc" stroke={kolor} strokeWidth="1.4" />
      <line x1={x} y1={y + s} x2={x + s} y2={y} stroke={kolor} strokeWidth="1.4" />
      {/* Prąd stały: kreska ciągła nad przerywaną */}
      <line x1={x + s * 0.14} y1={y + s * 0.28} x2={x + s * 0.46} y2={y + s * 0.28}
            stroke={kolor} strokeWidth="1.4" strokeLinecap="round" />
      <line x1={x + s * 0.14} y1={y + s * 0.4} x2={x + s * 0.46} y2={y + s * 0.4}
            stroke={kolor} strokeWidth="1.4" strokeDasharray="3 2.5" strokeLinecap="round" />
      {/* Prąd przemienny */}
      <path d={sinusoida} fill="none" stroke={kolor} strokeWidth="1.4" strokeLinecap="round" />
    </g>
  )
}

/** Dom na końcu wyjścia AC: rysunek czyta się szybciej niż samo słowo „Dom”. */
const Dom = ({ x, y }) => {
  const { t } = useTranslation('elektryka')
  return (
    <g>
      <path d={`M ${x} ${y + 9} L ${x + 12} ${y} L ${x + 24} ${y + 9} V ${y + 24} H ${x} Z`}
            fill="#ffffff" stroke={TEKST.cichy} strokeWidth="1.6" strokeLinejoin="round" />
      <rect x={x + 9} y={y + 14} width="6" height="10" rx="1" fill={TEKST.cichy} opacity="0.35" />
      <text x={x + 12} y={y + 38} textAnchor="middle" fontSize="10.5" fontWeight="600" fill={TEKST.zwykly}>
        {t('schemat.dom')}
      </text>
    </g>
  )
}

/**
 * Zgodność falownika z magazynem przy zejściu DC.
 *
 * Źródłem jest WYŁĄCZNIE moduł kompatybilności, który opiera się na danych
 * o konkretnych parach urządzeń. Nie wnioskujemy z tego, że oba są „wysoko-
 * napięciowe" ani że to ten sam producent — moduł kompatybilności celowo tego
 * nie robi i schemat nie ma prawa być mądrzejszy od niego.
 *
 * Znacznik rysujemy tylko dla stanów, o których coś WIEMY: potwierdzona para
 * (zielona fajka), para pod warunkami (żółty znak zapytania, jak „do sprawdzenia”
 * w raporcie) i para niezgodna według producenta (czerwony krzyżyk i przerywany
 * przewód, od 2026-09-30: wcześniej schemat rysował wtedy zwykłe połączenie,
 * choć raport oferty pokazywał czerwoną flagę). Przy „unknown” i braku danych
 * znacznika nie ma, a przewód jest szary: cisza jest uczciwsza niż fajka na domysł.
 */
// Opisy i podpis w słowniku `elektryka` (schemat.zgodnosc.<status>).
const STANY_ZGODNOSCI = {
  compatible: { kolor: '#059669', znak: 'fajka' },
  conditional: { kolor: '#d97706', znak: '?' },
  incompatible: { kolor: '#dc2626', znak: 'krzyzyk', podpis: true },
}

const ZnacznikKompatybilnosci = ({ x, y, status }) => {
  const { t } = useTranslation('elektryka')
  const s = STANY_ZGODNOSCI[status]
  if (!s) return null
  const opis = t(`schemat.zgodnosc.${status}`)
  return (
    <g role="img" aria-label={opis}>
      <title>{opis}</title>
      <circle cx={x} cy={y} r="8" fill={s.kolor} />
      {s.znak === 'fajka' && (
        <path d={`M ${x - 3.6} ${y + 0.2} l 2.4 2.6 l 4.8 -5.2`} fill="none" stroke="#ffffff"
              strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      )}
      {s.znak === 'krzyzyk' && (
        <path d={`M ${x - 3} ${y - 3} L ${x + 3} ${y + 3} M ${x + 3} ${y - 3} L ${x - 3} ${y + 3}`}
              stroke="#ffffff" strokeWidth="1.9" strokeLinecap="round" />
      )}
      {s.znak === '?' && (
        <text x={x} y={y + 3.8} textAnchor="middle" fontSize="11" fontWeight="700" fill="#ffffff">?</text>
      )}
    </g>
  )
}

/** Kolor przewodu DC i obrysu magazynu według stanu pary. */
const kolorPary = (status) => STANY_ZGODNOSCI[status]?.kolor || '#94a3b8'

/**
 * Magazyn energii pod falownikiem, narysowany jak bateria (biegun po prawej).
 *
 * Napięcie pokazujemy z KARTY KATALOGOWEJ modelu, gdy ją mamy — okno pracy
 * i wartość nominalną. Gdy karty brak, zostaje klasa napięciowa z katalogu
 * i typowy dla niej zakres, wyraźnie podpisany jako typowy (patrz
 * lib/podsumowanieZestawu). Rozróżnienie jest istotne, bo ten rysunek trafia
 * do PDF-a dla instalatora: „44,8–57,6 V z karty" i „typowo 48 V" to dla niego
 * dwie różne informacje, a nie ta sama liczba w innym opakowaniu.
 *
 * Czego tu NIE MA i mieć nie może: sprawdzenia, czy to napięcie mieści się
 * w oknie bateryjnym falownika. Tego parametru nie ma w żadnej naszej tabeli,
 * więc taki pasek byłby zmyśleniem. Zgodność pary bierze się wyłącznie z modułu
 * kompatybilności (patrz ZnacznikKompatybilnosci).
 */
const BlokMagazynu = ({ x, y, w, h, magazyn, status }) => {
  const { t } = useTranslation('elektryka')
  const kolor = kolorPary(status)
  const niezgodny = status === 'incompatible'
  const srodek = x + w / 2
  const etykieta = magazyn.napiecie?.etykieta
  const tytul = KLASY_MAGAZYNU[etykieta]
    ? t(KLASY_MAGAZYNU[etykieta])
    : `${t('schemat.magazyn')}${etykieta ? ` ${etykieta.toLowerCase()}` : ''}`
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="12" fill="#ffffff" filter="url(#vv-cien)"
            stroke={kolor} strokeWidth="1.6" strokeDasharray={niezgodny ? '5 4' : undefined} />
      <rect x={x + w} y={y + h / 2 - 9} width="5" height="18" rx="2" fill={kolor} />
      <text x={srodek} y={y + 17} textAnchor="middle" fontSize="10" fill={TEKST.cichy}>
        {tytul}
      </text>
      <text x={srodek} y={y + 36} textAnchor="middle" fontSize="16" fontWeight="700" fill={TEKST.mocny}>
        {pl(magazyn.pojemnoscKwh)} kWh
      </text>
      {magazyn.napiecie && (
        <>
          <text x={srodek} y={y + 52} textAnchor="middle" fontSize="9.5" fontWeight="600" fill={TEKST.zwykly}>
            {opisNapiecia(t, magazyn.napiecie.zakres)}
          </text>
          {/* Skąd wzięliśmy napięcie: to ON odróżnia wartość z karty katalogowej
              od typowej dla klasy urządzeń, więc musi być czytelny także na wydruku. */}
          <text x={srodek} y={y + 65} textAnchor="middle" fontSize="8.5" fill={TEKST.cichy}>
            {magazyn.napiecie.zrodlo !== 'karta'
              ? t('schemat.wartosc_typowa')
              : (magazyn.napiecie.nominalne
                ? t('schemat.z_karty', { nominalne: opisNapiecia(t, magazyn.napiecie.nominalne) })
                : t('schemat.z_karty_katalogowej'))}
          </text>
        </>
      )}
    </g>
  )
}

/**
 * Nazwy połaci przy dachu wschód–zachód.
 *
 * Podpisujemy łańcuchy, bo bez tego „2 łańcuchy po 9" wygląda jak dowolny podział,
 * a to jest podział NA POŁACIE: każda strona dachu ma własny łańcuch i własne
 * wejście MPPT. Przy trzech i więcej orientacjach nazw nie zgadujemy — numerujemy.
 */
const nazwaPolaci = (t, indeks, ileOrientacji) => {
  if (ileOrientacji === 2) return [t('schemat.wschod'), t('schemat.zachod')][indeks] ?? null
  if (ileOrientacji > 2) return t('schemat.polac', { n: indeks + 1 })
  return null
}

/** Plakietka z napięciem na przewodzie: biała, w kolorze wejścia MPPT. */
const Plakietka = ({ x, y, tekst, kolor }) => {
  const w = tekst.length * 7.2 + 14
  return (
    <g>
      <rect x={x - w / 2} y={y - 10} width={w} height="20" rx="10" fill="#ffffff" stroke={kolor.linia} strokeWidth="1.3" />
      <text x={x} y={y + 4} textAnchor="middle" fontSize="11.5" fontWeight="700" fill={kolor.ciemny}>{tekst}</text>
    </g>
  )
}

const SchematStringow = ({
  uklad, mpptCount, nazwaFalownika, nazwaModulu, mocAcKw, magazyn = null,
  kompatybilnosc = null,
}) => {
  const { t } = useTranslation('elektryka')
  if (!uklad) return null

  const { stringow, modulow_w_stringu: nWStringu, napiecie_mpp_v: napiecie } = uklad
  // Ile łańcuchów przypada na jedną połać — liczy backend (electrical/stringi.py).
  // `null` znaczy „nie udało się rozdzielić po równo", więc nie podpisujemy nic.
  const orientacje = uklad.orientacje || 1
  const naOrientacje = uklad.stringow_na_orientacje || null

  // Rozkład łańcuchów na wejścia — równomierny, bo przy jednakowych łańcuchach
  // nierówne obciążenie wejść nie ma uzasadnienia.
  const mppt = Math.max(1, Math.min(mpptCount || 1, stringow))
  const rozklad = Array.from({ length: mppt }, (_, i) =>
    Math.floor(stringow / mppt) + (i < stringow % mppt ? 1 : 0)).filter((n) => n > 0)

  // Powyżej 5 modułów rysujemy skrót — 20-modułowy łańcuch narysowany co do sztuki
  // rozjechałby schemat w poziomie i nic by nie wniósł.
  const MAX_RYSOWANYCH = 5
  const skrocony = nWStringu > MAX_RYSOWANYCH
  const rysowanych = skrocony ? 3 : nWStringu

  const W_MOD = 30
  const H_MOD = 46
  const ODSTEP = 8
  const X_START = 20
  const Y_START = 48
  const H_WIERSZA = 76
  const szerLancucha = rysowanych * (W_MOD + ODSTEP) + (skrocony ? 56 : 0)
  const X_FALOWNIK = X_START + szerLancucha + 96
  // 140, nie 116: przy węższym prostokącie typowa nazwa modelu („Deye
  // SUN-6K-SG05LP1-EU") schodziła do dwóch linii i najmniejszej czcionki.
  // Szerszy kształt nie psuje układu — schemat skaluje się do szerokości okna
  // przez `viewBox`, więc rośnie tylko w swoich własnych proporcjach.
  const W_FALOWNIK = 140
  const lancuchy = rozklad.reduce((a, b) => a + b, 0)
  // Magazyn wisi POD falownikiem, więc dokłada wysokości, a nie szerokości —
  // schemat ma się mieścić w oknie bez przewijania w poziomie.
  const H_MAGAZYNU = magazyn ? 76 : 0
  const ODSTEP_MAGAZYNU = magazyn ? 34 : 0
  const W_MAGAZYNU = W_FALOWNIK + 20
  const H = Math.max(Y_START + lancuchy * H_WIERSZA + 16, Y_START + 96) + H_MAGAZYNU + ODSTEP_MAGAZYNU + 8
  // Miejsce po prawej na wyjście AC i dom — bez niego falownik wygląda jak koniec
  // obwodu, a to on jest środkiem: moduły z jednej strony, instalacja domowa z drugiej.
  const SZER_WYJSCIA = 92
  const W = X_FALOWNIK + W_FALOWNIK + SZER_WYJSCIA + 16
  const Y_FALOWNIK = Y_START - 8
  // Dolna granica wysokości: przy jednym łańcuchu obudowa liczona z wierszy wyszłaby
  // niższa niż jej własna zawartość (nazwa + symbol + moc) i wszystko by się nałożyło.
  const H_FALOWNIK = Math.max(100, lancuchy * H_WIERSZA - H_WIERSZA + H_MOD + 16)
  // Nazwa łamana do szerokości kształtu, z zapasem 12 px na wewnętrzne marginesy.
  // Wcześniej była ucinana po 18 znakach — w połowie słowa i bez wielokropka,
  // więc dłuższe modele wychodziły poza prostokąt (patrz lib/zawijanieTekstu).
  const nazwa = zawinNazwe(nazwaFalownika || t('schemat.falownik'), { szerokosc: W_FALOWNIK - 12 })
  const WYS_LINII = 13

  // Symbol siedzi między nazwą u góry a mocą u dołu — i to JEGO oś jest wyjściem AC.
  // Górna granica przesuwa się w dół z każdą DODATKOWĄ linią nazwy, inaczej dwie
  // linie nachodziłyby na symbol przekształtnika.
  const S_SYMBOL = 34
  const yPodNazwa = Y_FALOWNIK + 24 + Math.max(0, nazwa.linie.length - 1) * WYS_LINII
  const ySymbol = (yPodNazwa + (Y_FALOWNIK + H_FALOWNIK - 20)) / 2 - S_SYMBOL / 2
  const yOsFalownika = ySymbol + S_SYMBOL / 2
  const xSrodkaFalownika = X_FALOWNIK + W_FALOWNIK / 2
  const statusPary = kompatybilnosc?.status
  const yMagazynu = Y_FALOWNIK + H_FALOWNIK + ODSTEP_MAGAZYNU

  let wiersz = -1

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      {/* BEZ minimalnej szerokości: schemat ma zmieścić się w oknie w całości.
          Wymuszone 560 px kazało przewijać go w poziomie, a rysunek przewijany
          w kawałkach przestaje być schematem — nie widać całej drogi od modułów
          do falownika. SVG skaluje się przez viewBox, więc traci tylko na wielkości
          czcionki, a nie na czytelności układu. */}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img"
           aria-label={t('schemat.aria', {
             stringow,
             n: nWStringu,
             mppt,
             lancuchy: t('liczby.lancuchy', { count: stringow }),
             moduly: t('liczby.moduly', { count: nWStringu }),
             wejscia: t('liczby.wejscia', { count: mppt }),
           })}>
        <defs>
          {/* Delikatny gradient modułu — płaski prostokąt wygląda jak makieta,
              a nie jak element schematu. */}
          {KOLORY_MPPT.map((k, i) => (
            <linearGradient key={i} id={`grad-mod-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor={k.tlo} />
            </linearGradient>
          ))}
          {/* Arkusz w kropki: rysunek techniczny, a nie kolejna ramka z tekstem. */}
          <pattern id="vv-kropki" width="14" height="14" patternUnits="userSpaceOnUse">
            <circle cx="1.5" cy="1.5" r="1" fill="#e2e8f0" />
          </pattern>
          <filter id="vv-cien" x="-20%" y="-20%" width="140%" height="150%">
            <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#0f172a" floodOpacity="0.1" />
          </filter>
        </defs>
        <rect x="0" y="0" width={W} height={H} fill="url(#vv-kropki)" />

        {/* Nagłówki kolumn — bez nich schemat wymaga legendy */}
        <text x={X_START} y={22} fontSize="11" fontWeight="600" fill={TEKST.najcichszy}>{t('schemat.naglowek_lancuchy')}</text>
        <text x={xSrodkaFalownika} y={22} textAnchor="middle" fontSize="11" fontWeight="600"
              fill={TEKST.najcichszy}>{t('schemat.naglowek_falownik')}</text>

        {/* Obudowa falownika */}
        <rect x={X_FALOWNIK} y={Y_FALOWNIK} width={W_FALOWNIK} height={H_FALOWNIK} rx="14"
              fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.5" filter="url(#vv-cien)" />
        {nazwa.linie.map((linia, i) => (
          <text key={linia} x={xSrodkaFalownika} y={Y_FALOWNIK + 18 + i * WYS_LINII}
                textAnchor="middle" fontSize={nazwa.rozmiar} fontWeight="700" fill={TEKST.mocny}>
            {linia}
          </text>
        ))}
        <SymbolPrzeksztaltnika x={xSrodkaFalownika - S_SYMBOL / 2} y={ySymbol} s={S_SYMBOL} />
        {mocAcKw != null && (
          <text x={xSrodkaFalownika} y={Y_FALOWNIK + H_FALOWNIK - 10}
                textAnchor="middle" fontSize="11" fontWeight="600" fill={TEKST.cichy}>
            {pl(mocAcKw)} kW AC
          </text>
        )}

        {/* Wyjście AC do instalacji domowej */}
        <line x1={X_FALOWNIK + W_FALOWNIK} y1={yOsFalownika}
              x2={X_FALOWNIK + W_FALOWNIK + SZER_WYJSCIA - 40} y2={yOsFalownika}
              stroke={TEKST.cichy} strokeWidth="2" strokeLinecap="round" />
        <text x={X_FALOWNIK + W_FALOWNIK + (SZER_WYJSCIA - 40) / 2} y={yOsFalownika - 8}
              textAnchor="middle" fontSize="10" fontWeight="600" fill={TEKST.cichy}>AC</text>
        <Dom x={X_FALOWNIK + W_FALOWNIK + SZER_WYJSCIA - 34} y={yOsFalownika - 16} />

        {/* Zejście DC z falownika do magazynu */}
        {magazyn && (
          <>
            <line x1={xSrodkaFalownika} y1={Y_FALOWNIK + H_FALOWNIK}
                  x2={xSrodkaFalownika} y2={yMagazynu}
                  stroke={kolorPary(statusPary)} strokeWidth="2" strokeLinecap="round"
                  strokeDasharray={statusPary === 'incompatible' ? '5 4' : undefined} />
            <text x={xSrodkaFalownika - (STANY_ZGODNOSCI[statusPary] ? 15 : 7)}
                  y={Y_FALOWNIK + H_FALOWNIK + ODSTEP_MAGAZYNU / 2 + 3.5} textAnchor="end"
                  fontSize="10" fontWeight="600" fill={TEKST.cichy}>DC</text>
            <ZnacznikKompatybilnosci x={xSrodkaFalownika} y={Y_FALOWNIK + H_FALOWNIK + ODSTEP_MAGAZYNU / 2}
                                     status={statusPary} />
            {STANY_ZGODNOSCI[statusPary]?.podpis && (
              <text x={xSrodkaFalownika + 14} y={Y_FALOWNIK + H_FALOWNIK + ODSTEP_MAGAZYNU / 2 + 3.5}
                    fontSize="10" fontWeight="700" fill={STANY_ZGODNOSCI[statusPary].kolor}>
                {t(`schemat.zgodnosc.${statusPary}_podpis`)}
              </text>
            )}
            <BlokMagazynu x={xSrodkaFalownika - W_MAGAZYNU / 2} y={yMagazynu}
                          w={W_MAGAZYNU} h={H_MAGAZYNU} magazyn={magazyn} status={statusPary} />
          </>
        )}

        {rozklad.map((ileLancuchow, iMppt) => {
          const k = KOLORY_MPPT[iMppt % KOLORY_MPPT.length]
          return Array.from({ length: ileLancuchow }, (_, iL) => {
            wiersz += 1
            const y = Y_START + wiersz * H_WIERSZA
            const yOs = y + H_MOD / 2
            const polac = naOrientacje && nazwaPolaci(t, Math.floor(wiersz / naOrientacje), orientacje)

            return (
              <g key={`${iMppt}-${iL}`}>
                {/* Etykieta łańcucha — z połacią, gdy dach ma kilka orientacji.
                    „Łańcuch", a nie „String": dokument czyta też klient końcowy, dla
                    którego angielskie słowo w polskim zdaniu jest kolejnym terminem do
                    odgadnięcia. Instalator rozpozna jedno i drugie. */}
                <text x={X_START} y={y - 9} fontSize="11.5" fontWeight="700" fill={TEKST.zwykly}>
                  {t('schemat.lancuch', { n: wiersz + 1 })}
                  {polac && <tspan fill={k.ciemny}>{' '}({polac})</tspan>}
                  <tspan fill={TEKST.najcichszy} fontWeight="400"> · {t('liczby.moduly', { count: nWStringu })}</tspan>
                </text>

                {/* Moduły połączone szeregowo — linia łącząca POD spodem pokazuje,
                    że to jeden obwód, a nie luźne prostokąty */}
                <line x1={X_START + W_MOD / 2} y1={yOs}
                      x2={X_START + (rysowanych - 1) * (W_MOD + ODSTEP) + W_MOD / 2} y2={yOs}
                      stroke={k.linia} strokeWidth="1.5" opacity="0.35" />
                {Array.from({ length: rysowanych }, (_, i) => (
                  <ModulPv key={i} x={X_START + i * (W_MOD + ODSTEP)} y={y}
                           w={W_MOD} h={H_MOD} kolor={k}
                           gradient={`url(#grad-mod-${iMppt % KOLORY_MPPT.length})`} />
                ))}

                {skrocony && (
                  <>
                    <line x1={X_START + rysowanych * (W_MOD + ODSTEP) - ODSTEP} y1={yOs}
                          x2={X_START + rysowanych * (W_MOD + ODSTEP) + 44} y2={yOs}
                          stroke={k.linia} strokeWidth="1.5" strokeDasharray="3 3" opacity="0.5" />
                    <text x={X_START + rysowanych * (W_MOD + ODSTEP) + 22} y={yOs - 7}
                          textAnchor="middle" fontSize="12" fontWeight="700" fill={k.ciemny}>
                      +{nWStringu - rysowanych}
                    </text>
                  </>
                )}

                {/* Przewód do falownika z napięciem jako plakietką na nim */}
                <line x1={X_START + szerLancucha} y1={yOs} x2={X_FALOWNIK} y2={yOs}
                      stroke={k.linia} strokeWidth="2" strokeLinecap="round" />
                <circle cx={X_FALOWNIK} cy={yOs} r="3.5" fill={k.linia} />
                <Plakietka x={(X_START + szerLancucha + X_FALOWNIK) / 2} y={yOs}
                           tekst={`${Math.round(napiecie)} V`} kolor={k} />
                <text x={(X_START + szerLancucha + X_FALOWNIK) / 2} y={yOs + 24}
                      textAnchor="middle" fontSize="10" fill={TEKST.najcichszy}>
                  MPPT {iMppt + 1}
                </text>
              </g>
            )
          })
        })}
      </svg>
    </div>
  )
}

export default SchematStringow
